import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { SKIP_AUTH } from '../../services/api.service';

// Endpoints that need an AM admin token but NOT a per-user AM_DB token.
// (Login & register issue the AM_DB token but cannot be called with one yet,
// and the public catalog/static endpoints are anonymous.)
const NO_USER_TOKEN_PATHS = [
    '/website-login',
    '/website-register',
    '/send-otp',
    '/verify-otp',
    '/forgot-password',
    '/reset-password',
    '/home-data',
    '/products',
    '/product-detail',
    '/search',
    '/categories',
    '/brands',
    '/banners',
    '/faqs',
    '/static-page',
    '/product-reviews',
    '/check-pincode',
    '/contact',
    '/schema/query',
];

function buildHeaders(auth: AuthService, url: string): Record<string, string> {
    const headers: Record<string, string> = {};
    const amToken = auth.getToken();
    if (amToken) headers['x-am-authorization'] = amToken;

    const needsUserToken = !NO_USER_TOKEN_PATHS.some(p => url.includes(p));
    if (needsUserToken) {
        const userToken = auth.getUserToken();
        if (userToken && userToken !== 'undefined') {
            headers['x-am-user-authorization'] = userToken;
        }
    }
    return headers;
}

function isAmTokenError(err: HttpErrorResponse): boolean {
    const msg = (err?.error?.errors?.[0]?.message || err?.error?.message || '') + '';
    return /x-am-authorization/i.test(msg);
}

function isAmDbTokenError(err: HttpErrorResponse): boolean {
    const msg = (err?.error?.errors?.[0]?.message || err?.error?.message || '') + '';
    return /x-am-user-authorization/i.test(msg);
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    // The /system-api/{userPath}/token bootstrap call sets SKIP_AUTH because it
    // has no token to attach yet.
    if (req.context.get(SKIP_AUTH)) return next(req);

    const auth = inject(AuthService);
    const headers = buildHeaders(auth, req.url);
    const cloned = req.clone({ setHeaders: headers });

    return next(cloned).pipe(
        catchError((err: HttpErrorResponse) => {
            if (err.status !== 401) return throwError(() => err);

            // 401 specifically about the AM admin token: it's stale/expired.
            // Wipe the cached one, fetch a fresh one, and retry the request once.
            if (isAmTokenError(err)) {
                return from(refreshAmTokenAndRetry(auth, req, next));
            }

            // 401 about the per-user AM_DB token: the user session expired.
            if (isAmDbTokenError(err)) {
                auth.sessionExpired();
                return throwError(() => err);
            }

            // Generic 401 (no specific message we can act on) — treat as session
            // expiry to be safe.
            auth.sessionExpired();
            return throwError(() => err);
        }),
    );
};

async function refreshAmTokenAndRetry(
    auth: AuthService,
    originalReq: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Promise<any> {
    // Drop the stale token from memory + storage so ensureAMToken refetches.
    auth.clearAmToken();
    const fresh = await auth.ensureAMToken();
    if (!fresh) throw new Error('Failed to refresh AM token');

    const retryHeaders = buildHeaders(auth, originalReq.url);
    const retryReq = originalReq.clone({ setHeaders: retryHeaders });
    // Convert the inner observable into a promise so the outer `from(...)` can
    // emit the response (or error) like a normal request.
    return new Promise((resolve, reject) => {
        next(retryReq).subscribe({
            next: (event) => resolve(event),
            error: (err) => reject(err),
        });
    });
}
